import { isEffectivelyDisconnected } from './accountConnection'
import { STALE_AFTER_MS, type Account } from './useAccounts'

export { isEffectivelyDisconnected }

/**
 * `Health.kind` is its own discriminant word, deliberately not `status` —
 * `AsyncState`'s own discriminant IS `status`, and these are two different
 * types answering two different questions (whether a REQUEST succeeded vs.
 * whether an ACCOUNT'S bank connection is current). Unifying the words would
 * make one read like a case of the other.
 */
export type Health =
  | { kind: 'ok'; lastSyncedAt: Date | null }
  | { kind: 'manual' }
  | { kind: 'stale'; since: Date }
  | { kind: 'reauth'; institution: string | null; since: Date | null }
  | { kind: 'disconnected'; reconnectable: boolean }

/**
 * The ONE interpreter of connection health. A group's health
 * (`connectionGroups.ts#healthFor`) is this function over the group's first
 * account — there is no second derivation. Connections (Ticket 3) reads
 * this same function, so a Flinks-vs-Plaid difference is a change here and
 * not a grep across components.
 *
 * **Reads `Account`, already mapped by `toAccount()` in `useAccounts.ts` —
 * never a wire field.** The plan and issue #396 both said this accessor is
 * "the ONE place the `plaid_*` fields may be read"; that premise predates
 * #402, which switched `useAccounts.ts` onto the provider-neutral
 * `item_error_code`/`item_action_required` names and lifted them onto
 * `Account.errorCode`/`Account.actionRequired`. There are no `plaid_*` reads
 * left in `src/` to inherit, and adding one here would reintroduce exactly
 * what this function exists to avoid. The invariant this preserves:
 * `toAccount()` is the one wire reader, `accountHealth()` is the one health
 * interpreter — a narrower boundary than the plan named, but the same split.
 *
 * **A manual account is `manual`, never `ok` with a null `lastSyncedAt`.** It
 * has no sync at all, so "last synced: never" is a category error that would
 * render a stale banner with no way to ever clear.
 *
 * **Precedence: manual, then disconnected, then reauth, then stale/ok.**
 * `connectionStatus === 'disconnected'` outranks reauth because a bank the
 * user (or its provider) removed cannot be repaired, only reconnected. The
 * `actionRequired`/`errorCode` clause of reauth is kept alongside
 * `reauth_required` until the backend confirms `connection_status` covers
 * every case those two fields do.
 *
 * **Reauth outranks stale, checked first.** Both can be true on the same
 * account — a login problem that has persisted long enough for the sync to
 * also be old — and showing "stale" in that case tells the user to wait when
 * they need to act instead.
 *
 * **An error code alone (`actionRequired` still `null`) still surfaces as
 * `reauth`.** `Health` has no separate "error" kind — the row and detail
 * screen's response to any item problem is identical, offer Reconnect — so
 * either field being set is read as the same fact.
 *
 * **`item_status` is never read here (N8, architect review, #396).**
 * `toAccount()` does not lift it onto `Account` either, so an item reporting
 * `login_required` there with a null `errorCode`/`actionRequired` would read
 * as `ok` — a gap, not a considered exclusion. Whether the backend can
 * actually produce that combination is an open question to the backend, not
 * yet answered; noted here rather than left silent so the gap is found by
 * reading this docblock instead of by a user seeing a healthy-looking
 * account that needs reconnecting.
 */
export function accountHealth(a: Account): Health {
  if (a.provider === 'manual') return { kind: 'manual' }

  // `connection_status` is the ONE source of "is this bank still connected".
  // Never `item.status`, never an item's absence from `GET /aggregator/items`:
  // a provider-removed item is still listed there, a user-disconnected one is
  // not, and both are disconnected. It outranks everything below — a
  // leftover error code on a disconnected account is not a repair to offer.
  // A null `connection_status` on a non-manual account is the backend's
  // "provider-linked row whose item is gone altogether" (a dangling row): no
  // connection exists to be healthy, so it reads disconnected, never `ok`.
  // Derived from the status, not from the item's absence.
  if (isEffectivelyDisconnected(a)) {
    return { kind: 'disconnected', reconnectable: a.reconnectable }
  }

  if (a.connectionStatus === 'reauth_required' || a.actionRequired !== null || a.errorCode !== null) {
    return { kind: 'reauth', institution: a.institutionName, since: a.lastSyncedAt }
  }

  if (a.lastSyncedAt !== null && Date.now() - a.lastSyncedAt.getTime() > STALE_AFTER_MS) {
    return { kind: 'stale', since: a.lastSyncedAt }
  }

  return { kind: 'ok', lastSyncedAt: a.lastSyncedAt }
}
